import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = [
    "status",
    "progressSection",
    "progressBar",
    "progressText",
    "progressCount",
    "phaseLabel",
    "message",
    "totalRecords",
    "successfulRecords",
    "failedRecords",
    "successRate"
  ]

  static values = {
    batchId: Number
  }

  connect() {
    this.subscription = this.createSubscription()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  createSubscription() {
    const controller = this

    return createConsumer().subscriptions.create(
      { channel: "MigrationProgressChannel", batch_id: this.batchIdValue },
      {
        connected() {
          console.log("Connected to MigrationProgressChannel for batch", controller.batchIdValue)
        },

        disconnected() {
          console.log("Disconnected from MigrationProgressChannel")
        },

        received(data) {
          controller.handleUpdate(data)
        }
      }
    )
  }

  handleUpdate(data) {
    console.log("Migration progress update:", data)

    // Update status badge
    if (this.hasStatusTarget && data.status) {
      this.statusTarget.textContent = this.humanize(data.status)
      this.statusTarget.className = `inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${this.statusBadgeClass(data.status)}`
    }

    // Update progress bar and text
    if (data.progress !== undefined) {
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = `${data.progress}%`
      }
      if (this.hasProgressTextTarget) {
        this.progressTextTarget.textContent = `${data.progress}%`
      }
    }

    // Update progress count
    if (this.hasProgressCountTarget && data.processed_records !== undefined) {
      const total = data.total_records || this.totalRecordsTarget?.textContent || 0
      this.progressCountTarget.textContent = `${data.processed_records} / ${total} records`
    }

    // Update phase label
    if (this.hasPhaseLabelTarget && data.phase) {
      this.phaseLabelTarget.textContent = `${this.humanize(data.phase)}...`
    }

    // Update message
    if (this.hasMessageTarget && data.message) {
      this.messageTarget.textContent = data.message
    }

    // Update summary cards
    if (this.hasTotalRecordsTarget && data.total_records !== undefined) {
      this.totalRecordsTarget.textContent = data.total_records
    }

    if (this.hasSuccessfulRecordsTarget && data.successful_records !== undefined) {
      this.successfulRecordsTarget.textContent = data.successful_records
    }

    if (this.hasFailedRecordsTarget && data.failed_records !== undefined) {
      this.failedRecordsTarget.textContent = data.failed_records
    }

    if (this.hasSuccessRateTarget && data.success_rate !== undefined) {
      this.successRateTarget.textContent = `${data.success_rate}%`
    }

    // Handle completion
    if (data.status === "completed" || data.status === "failed" || data.status === "rolled_back") {
      // Show notification
      if (window.showNotification) {
        const type = data.status === "completed" ? "success" : data.status === "failed" ? "error" : "info"
        window.showNotification(data.message || `Migration ${data.status}`, type)
      }

      // Reload page after a short delay to show final state
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    }
  }

  statusBadgeClass(status) {
    const classes = {
      pending: "bg-gray-100 text-gray-800",
      created: "bg-gray-100 text-gray-800",
      validating: "bg-yellow-100 text-yellow-800",
      validated: "bg-blue-100 text-blue-800",
      importing: "bg-yellow-100 text-yellow-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
      rolling_back: "bg-yellow-100 text-yellow-800",
      rolled_back: "bg-gray-100 text-gray-800"
    }
    return classes[status] || "bg-gray-100 text-gray-800"
  }

  humanize(str) {
    return str
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }
}
